import { useToolSchemaLookup } from '@cotera/client/app/hooks/use-tool-schema';
import {
  DisplayError,
  Loading,
  ModalPageView,
  useModalManager,
} from '@cotera/client/app/components';
import { useTenantedQueryKey } from '@cotera/client/app/hooks/use-tenanted-query-key';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useWatchableValue } from '@cotera/client/v0/actions/framework/watchable';
import { useShowToolSettingsModal } from '@cotera/client/v0/components/tool-connections/tool-connections-dialog';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { Button as NewButton } from '@cotera/client/v0/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@cotera/client/v0/components/ui/tabs';
import { Assert } from '@cotera/utilities';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Plus, XIcon } from 'lucide-react';
import { Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useColumnModel } from '../context';
import { ConfigSection } from './common';
import { useAnalytics } from '@cotera/client/app/etc';
import _ from 'lodash';
import { Ty } from '@cotera/era';

const useSavedTools = () => {
  const toolsQueryKey = useTenantedQueryKey(['tools']);
  const connectionsQueryKey = useTenantedQueryKey(['tools', 'connections']);
  const client = useTenantedClient()['tools'];

  const { data: tools } = useSuspenseQuery({
    queryKey: toolsQueryKey,
    queryFn: async () => {
      return Assert.assertOk(await client.list({}));
    },
  });

  const { data: connections } = useSuspenseQuery({
    queryKey: connectionsQueryKey,
    queryFn: async () => {
      return Assert.assertOk(await client.listConnections({}));
    },
  });

  // Sort tools with configured connections first
  const sortedTools = useMemo(() => {
    return tools.sort((a, b) => {
      const aHasConnection =
        a.toolConnectionId !== null &&
        connections.some((conn) => conn.id === a.toolConnectionId);
      const bHasConnection =
        b.toolConnectionId !== null &&
        connections.some((conn) => conn.id === b.toolConnectionId);

      // If both have connections or both don't, sort alphabetically
      if (aHasConnection === bHasConnection) {
        return a.displayName.localeCompare(b.displayName);
      }

      // Tools with connections come first
      return aHasConnection ? -1 : 1;
    });
  }, [tools, connections]);

  return { data: sortedTools };
};

const ToolParamsModal: React.FC<{ toolName: string }> = ({ toolName }) => {
  const toolSchemaLookup = useToolSchemaLookup();
  const toolSchema = toolSchemaLookup(toolName);
  Assert.assert(toolSchema !== null, 'Tool not found');

  return (
    <ModalPageView
      className="px-4 py-4"
      title={toolName}
      subtitle={`Input and output schemas for the ${toolName} tool`}
    >
      <Tabs defaultValue="inputs" className="w-full">
        <TabsList className="w-full justify-end gap-4 shrink-0">
          <TabsTrigger value="inputs">Input</TabsTrigger>
          <TabsTrigger value="outputs">Output</TabsTrigger>
        </TabsList>
        <div className="max-h-[400px] overflow-auto">
          <TabsContent value="inputs">
            <span className="text-sm text-muted-foreground">
              Based on your prompt, the agent will use data that looks like this
              to call the tool. Make sure that all required parameters are
              accounted for!
            </span>
            <pre className="text-sm p-2 my-2 ">
              {toolSchema.inputSchema.prettyPrint()}
            </pre>
          </TabsContent>
          <TabsContent value="outputs">
            <span className="text-sm text-muted-foreground">
              The tool will return data that looks like this.
            </span>
            <pre className="text-sm p-2 my-2 ">
              {Ty.displayTy(toolSchema.outputSchema.ty)}
            </pre>
          </TabsContent>
        </div>
      </Tabs>
    </ModalPageView>
  );
};

const useShowToolParams = () => {
  const manager = useModalManager((s) => s.actions);

  return (toolName: string) => {
    manager.push({
      view: () => <ToolParamsModal toolName={toolName} />,
    });
  };
};

const ToolList: React.FC = () => {
  const model = useColumnModel();
  const { tools: selectedTools } = useWatchableValue(model.formState);
  const { data: tools } = useSavedTools(); // Already sorted by useSavedTools
  const showToolParams = useShowToolParams();

  return (
    <div className="flex gap-2 flex-wrap">
      {selectedTools.map((toolId) => {
        const tool = tools.find((t) => t.id === toolId);
        if (tool === undefined) {
          return null;
        }
        return (
          <Badge
            variant="outline"
            className="gap-0 px-2 py-1 hover:bg-accent/40 text-foreground/70 cursor-pointer"
            key={toolId}
            onClick={() => {
              showToolParams(tool.name);
            }}
          >
            {tool?.name}
            <button
              className="focus-visible:border-ring focus-visible:ring-ring/50 text-muted-foreground/60 hover:text-muted-foreground -my-[5px] -ms-0.5 -me-2 inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-[inherit] p-0 transition-[color,box-shadow] outline-none focus-visible:ring-[3px]"
              onClick={(e) => {
                e.stopPropagation();
                model.formState.set({
                  ...model.formState.snapshot(),
                  tools: model.formState
                    .snapshot()
                    .tools.filter((t) => t !== toolId),
                });
              }}
              aria-label="Delete"
            >
              <XIcon aria-hidden="true" className="shrink-0 size-4" />
            </button>
          </Badge>
        );
      })}
    </div>
  );
};

export const Tools: React.FC = () => {
  const model = useColumnModel();
  const queryKey = useTenantedQueryKey(['tools']);
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const showToolSettingsModal = useShowToolSettingsModal(async (tool) => {
    await queryClient.invalidateQueries({ queryKey });
    model.formState.set({
      ...model.formState.snapshot(),
      tools: [...model.formState.snapshot().tools, tool.id],
    });
  });

  return (
    <ConfigSection title="Tools">
      <div className="flex gap-2 w-full items-center">
        <ErrorBoundary fallbackRender={(e) => <DisplayError error={e.error} />}>
          <Suspense
            fallback={
              <div className="flex gap-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Loading.Shimmer key={i} className="w-[40px] h-6" />
                ))}
              </div>
            }
          >
            <ToolList />
          </Suspense>
        </ErrorBoundary>

        <NewButton
          size="icon"
          variant="ghost"
          onClick={() => {
            analytics.track('Tool Added', {
              source: 'llm_column_tools',
            });
            showToolSettingsModal();
          }}
        >
          <Plus />
        </NewButton>
      </div>
    </ConfigSection>
  );
};
